import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMedia } from '@/hooks';
import { hasTranslation } from '@/modules/localization';
import { replaceFsCount, useButtonAction } from '@/modules/promo';
import { useFsCountFromQuery } from '@/modules/promo/hooks/useFsCountFromQuery';
import { Button, CopyCode, Logo } from '../../components';
import { FlyingElement } from '../../components/FlyingElements';
import { Loader } from '../../components/Loader';
import type { ScLogoProps } from '../../components/Logo/styled';
import type {
  FlyingElementSrc as FlyingElementType,
  ImagesProps,
} from '../../types';
import { CookiePopup } from './components/CookiePopup';
import { HeroV2 } from './components/HeroV2';
import { TermsAndConditions } from './components/Terms&Conds';
import { ScBannerTemplate, type ScBannerTemplateProps } from './styled';

interface BannerTemplateProps
  extends ScBannerTemplateProps,
    ScLogoProps,
    ImagesProps {
  translation: string;
  isRecommendedByIncluded?: boolean;
  code?: string;
  isWelcomeAndNDBIncluded?: boolean;
  spinsText?: number;
  isBlackFriday?: boolean;
  isSlot?: boolean;
  loadingText?: boolean;
  bonusName?: string;
  withTerms?: boolean;
  claimButtonMode?: 'primary' | 'orangeRedGradient';
  withCookiePopup?: boolean;
  flyingElements?: FlyingElementType[];
  signUpBtn?: boolean;
  copyCodeTitle?: string;
  validDate?: string;
}

export const BannerTemplate = ({
  translation,
  isRecommendedByIncluded,
  backgroundImageUrl,
  backgroundMobUrl,
  heroImageUrl,
  flyingImageUrls,
  flyingElements,
  code,
  isWelcomeAndNDBIncluded,
  spinsText,
  leftImageUrl,
  leftImgWithoutPadding,
  isImagesHidden,
  staticImageMobile,
  prizeImages,
  bonusName,
  imageForCentralBanner,
  withTerms = true,
  isSlot,
  flyingImageConfig,
  logo,
  logoSize,
  claimButtonMode = 'primary',
  withCookiePopup,
  signUpBtn,
  copyCodeTitle,
  validDate,
}: BannerTemplateProps) => {
  const { t } = useTranslation([translation, 'common']);
  const { isMobile } = useMedia();
  const { buttonAction, isLoading } = useButtonAction();

  const claimButtonProps = useMemo(() => {
    switch (claimButtonMode) {
      case 'orangeRedGradient':
        return {
          orangeRedGradient: true,
        };
      default:
        return {
          primary: true,
        };
    }
  }, [claimButtonMode]);

  const claimButton = useMemo(
    () => (
      <Button disabled={isLoading} {...claimButtonProps} {...buttonAction}>
        {isLoading ? <Loader /> : t('common:claimNow')}
      </Button>
    ),
    [buttonAction, claimButtonProps, isLoading, t]
  );

  const fsCount = useFsCountFromQuery();

  return (
    <ScBannerTemplate
      backgroundImageUrl={backgroundImageUrl}
      backgroundMobUrl={backgroundMobUrl}
      flyingImageConfig={flyingImageConfig}
    >
      {!isImagesHidden && (
        <FlyingElement flyingElements={flyingElements} maxWidthMobile={1015} />
      )}
      {isImagesHidden && imageForCentralBanner ? (
        <div className={`hero ${isImagesHidden ? 'imageHidden' : ''}`}>
          <HeroV2
            button={claimButton}
            code={code}
            copyCodeTitle={copyCodeTitle}
            image={imageForCentralBanner}
            isImagesHidden={isImagesHidden}
            logo={logo}
            logoSize={logoSize}
            spins={spinsText}
            text={bonusName}
            translation={translation}
          >
            <FlyingElement
              flyingElements={flyingElements}
              maxWidthMobile={768}
            />
          </HeroV2>
          <div className="button-terms-box">
            {!isMobile && claimButton}
            {withTerms && (
              <TermsAndConditions
                title={t('common:button.termsAndConditions')}
                translation={translation}
                validDate={validDate}
              />
            )}
          </div>
          {withCookiePopup && (
            <CookiePopup
              claimButtonMode={claimButtonMode}
              translation={translation}
            />
          )}
        </div>
      ) : (
        <div
          className={`hero ${leftImageUrl ? 'withLeftImage' : ''}
          ${leftImgWithoutPadding ? 'leftImgWithoutPadding' : ''}
        `}
        >
          <Logo />
          <div className="hero__content">
            {isRecommendedByIncluded && (
              <p className="hero__recommended">
                {t('recommended')}{' '}
                <img alt="Ask gamblers" src="/images/ask-gamblers.svg" />
              </p>
            )}
            <p className="hero__welcome">
              <Trans
                i18nKey={`${translation}:welcome`}
                tOptions={{ count: spinsText }}
              />
            </p>
            {hasTranslation(t, 'welcome-bonus') && (
              <p className="welcome-bonus">{t('welcome-bonus')}</p>
            )}
            {hasTranslation(t, 'description') && (
              <p className="description">{t('description')}</p>
            )}
            {hasTranslation(t, 'bonus') && (
              <p className="hero__text">
                {spinsText
                  ? t('bonus', { count: fsCount || spinsText })
                  : replaceFsCount(t('bonus'), fsCount)}
                {isWelcomeAndNDBIncluded && (
                  <>
                    <br />
                    {t('ndb')}
                  </>
                )}
              </p>
            )}
            {isSlot && <p className="hero__welcome">{t('slot')}</p>}
          </div>

          {code && (
            <div className="hero__code">
              <CopyCode code={code} />
            </div>
          )}
          <div className="button-terms-box">
            <Button disabled={isLoading} primary {...buttonAction}>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  {t('regLink') !== 'regLink'
                    ? t('regLink')
                    : signUpBtn
                      ? t('common:button.signUpLp')
                      : t('common:button.getBonus')}
                </>
              )}
            </Button>

            {withTerms && (
              <TermsAndConditions
                title={t('common:button.termsAndConditions')}
                translation={translation}
                validDate={validDate}
              />
            )}
          </div>

          {prizeImages && (
            <div className="hero__prizes">
              {prizeImages?.map((prizeSrc, index) => (
                <img alt={`prize-${index + 1}`} key={index} src={prizeSrc} />
              ))}
            </div>
          )}
        </div>
      )}
      {!isImagesHidden &&
        (leftImageUrl || leftImgWithoutPadding ? (
          <div className="banner__image">
            <img
              alt="Left"
              className="desktop"
              src={leftImageUrl || leftImgWithoutPadding}
            />
            <img alt="Static" className="desktop" src={heroImageUrl} />
            <img alt="Static" className="mobile" src={staticImageMobile} />
          </div>
        ) : (
          <div className="bgImg">
            {flyingImageUrls?.map((image, index) => (
              <img alt="flying" className="flying" key={index} src={image} />
            ))}
            <img alt="hero" className="heroImg" src={heroImageUrl} />
          </div>
        ))}
    </ScBannerTemplate>
  );
};
